import httpService from '../httpService';
import {
  BalanceSheet,
  BalanceSheetWithBookYear,
  BalanceStats,
  BankAccount,
  BankMutation,
  BankTransactionWithParticipation,
  BookYear,
  Budget,
  BudgetReport,
  CategorizationRule,
  CategorizationRuleApplyResult,
  ContributionPlanModel,
  ContributionPlanWithMembersModel,
  ContributionPlanWithPayments,
  CreateBankAccountModel,
  CreateBankTransactionModel,
  CreateBookYearDto,
  CreateCategorizationRuleDto,
  CreateContributionPaymentDto,
  CreateContributionPlanDto,
  CreateFinancialCategoryDto,
  CreateJournalEntryDto,
  CreateSavingPotDto,
  Currency,
  FinancialCategory,
  FundSavingPotDto,
  GoCardlessAccount,
  GoCardlessInstitution,
  GoCardlessLink,
  InternalBankTransfer,
  JournalEntryModel,
  LedgerAccount,
  ProfitAndLoss,
  ProfitAndLossDetailed,
  ReleaseSavingPotDto,
  RunContributionPlansResponse,
  SavingPot,
  SavingPotBalanceResponse,
  SavingPotWithParticipants,
  UpdateCategorizationRuleDto,
  UpdateBookYearDto,
  UpdateFinancialCategoryDto,
  UpdateSavingPotDto,
  UpsertBudgetDto,
  WalletBalanceResponse,
} from '../../types/bankAccount';
import { RootState } from '../../redux/reducers';
import { store } from '../../redux/store';

export async function createBankAccount(bankAccount: CreateBankAccountModel): Promise<BankAccount> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  const { data } = await httpService.post<BankAccount>(
    `/networks/dashboards/${selectedDashboard}/bank-accounts`,
    bankAccount,
  );

  return data;
}

export async function deleteBankAccount(accountId: number): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  await httpService.delete<BankAccount>(`/networks/dashboards/${selectedDashboard}/bank-accounts/${accountId}`);
}

export async function getAllBankAccounts(): Promise<BankAccount[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  const { data } = await httpService.get<BankAccount[]>(`/networks/dashboards/${selectedDashboard}/bank-accounts`);
  return data;
}

export async function getAllMutationsForBank(accountId: number): Promise<BankMutation[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .get<BankMutation[]>(`/networks/dashboards/${selectedDashboard}/bank-accounts/${accountId}/mutations`)
    .then((res) => res.data);
}

/**
 * @deprecated Use createJournalEntry instead
 */
export async function createTransactionWithParticipations(
  transactionData: CreateBankTransactionModel,
  isSystemProcessing: boolean = false,
): Promise<BankTransactionWithParticipation> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post(
      `/networks/dashboards/${selectedDashboard}/transactions`,
      transactionData,
      {
        headers: {
          'x-system-processing': String(isSystemProcessing),
        },
      },
    )
    .then((res) => res.data);
}

export async function createJournalEntry(
  journalEntryData: CreateJournalEntryDto,
  isSystemProcessing: boolean = false,
): Promise<JournalEntryModel> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post(
      `/networks/dashboards/${selectedDashboard}/journal-entries`,
      journalEntryData,
      {
        headers: {
          'x-system-processing': String(isSystemProcessing),
        },
      },
    )
    .then((res) => res.data);
}

/**
 * @deprecated Use createJournalEntry for individual entries instead
 */
export async function createTransactionsWithParticipations(
  transactionData: CreateBankTransactionModel[],
  isSystemProcessing: boolean = false,
): Promise<number[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post(`/networks/dashboards/${selectedDashboard}/transactions`, 
      transactionData, 
      {
        headers: {
          'x-system-processing': String(isSystemProcessing),
        },
      }
    )
    .then((res) => res.data);
}

/**
 * @deprecated Use updateJournalEntry instead
 */
export async function updateBankTransaction(
  transactionId: number,
  transactionData: CreateBankTransactionModel,
  isSystemProcessing: boolean = false,
): Promise<BankTransactionWithParticipation> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .put<BankTransactionWithParticipation>(
      `/networks/dashboards/${selectedDashboard}/transactions/${transactionId}`,
      transactionData,
      {
        headers: {
          'x-system-processing': String(isSystemProcessing),
        },
      }
    )
    .then((res) => res.data);
}

export async function updateJournalEntry(
  journalEntryId: number,
  journalEntryData: Partial<CreateJournalEntryDto>,
  isSystemProcessing: boolean = false,
): Promise<JournalEntryModel> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .put<JournalEntryModel>(
      `/networks/dashboards/${selectedDashboard}/journal-entries/${journalEntryId}`,
      journalEntryData,
      {
        headers: {
          'x-system-processing': String(isSystemProcessing),
        },
      }
    )
    .then((res) => res.data);
}

export async function getDutchBanks(): Promise<GoCardlessInstitution[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .get<GoCardlessInstitution[]>(`/networks/dashboards/${selectedDashboard}/go-cardless/institutions`)
    .then((res) => res.data);
}

export async function createGoCardlessLink(
  accountId: number,
  institutionId: string,
  lang: 'en' | 'nl',
): Promise<GoCardlessLink> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post<GoCardlessLink>(
      `/networks/dashboards/${selectedDashboard}/bank-accounts/${accountId}/go-cardless/links`,
      {
        institutionId,
        lang,
      },
    )
    .then((res) => res.data);
}

export async function getAccountsByRequisition(requisitionId: string) {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .get<
      GoCardlessAccount[]
    >(`/networks/dashboards/${selectedDashboard}/requisition/${requisitionId}/accounts`)
    .then((res) => res.data);
}

export async function createGoCardlessTransactions(
  goCardlessAccountId: string,
  veraAccountId: number,
  fromDate?: string,
) {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post(
      `/networks/dashboards/${selectedDashboard}/bank-accounts/${veraAccountId}/go-cardless/transactions/sync-initial`,
      {
        goCardlessAccountId,
        fromDate,
      },
    )
    .then((res) => res.data);
}

export async function fetchLatestGoCardlessTransactions(accountId: number) {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post(
      `/networks/dashboards/${selectedDashboard}/bank-accounts/${accountId}/go-cardless/transactions/sync-latest`,
    )
    .then((res) => res.data);
}

export async function updateBankAccountAutoFetch(
  accountId: number,
  autoFetchEnabled: boolean,
): Promise<BankAccount> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  const { data } = await httpService.patch<BankAccount>(
    `/networks/dashboards/${selectedDashboard}/bank-accounts/${accountId}/auto-fetch`,
    {
      autoFetchEnabled,
    },
  );
  return data;
}

export async function unlinkAccountFromGoCardless(accountId: number) {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService.delete(`/networks/dashboards/${selectedDashboard}/bank-accounts/${accountId}/requisition`);
}

/**
 * @deprecated Use getAllJournalEntries instead
 */
export async function getAllTransactions(): Promise<BankTransactionWithParticipation[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .get<BankTransactionWithParticipation[]>(`/networks/dashboards/${selectedDashboard}/transactions`)
    .then((res) => {
      return res.data;
    });
}

export async function getAllJournalEntries(params?: {
  sourceType?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<JournalEntryModel[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  const searchParams = new URLSearchParams();
  if (params?.sourceType) searchParams.set('sourceType', params.sourceType);
  if (params?.fromDate) searchParams.set('fromDate', params.fromDate);
  if (params?.toDate) searchParams.set('toDate', params.toDate);
  const queryString = searchParams.toString();
  return httpService
    .get<JournalEntryModel[]>(
      `/networks/dashboards/${selectedDashboard}/journal-entries${queryString ? `?${queryString}` : ''}`,
    )
    .then((res) => res.data);
}

export async function getJournalEntry(journalEntryId: number): Promise<JournalEntryModel | null> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .get<JournalEntryModel | null>(
      `/networks/dashboards/${selectedDashboard}/journal-entries/${journalEntryId}`,
    )
    .then((res) => res.data);
}

/**
 * @deprecated Use deleteJournalEntry instead
 */
export async function deleteTransaction(transactionId: number): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  await httpService.delete(
    `/networks/dashboards/${selectedDashboard}/transactions/${transactionId}`,
    { validateStatus: (status) => status >= 200 && status < 300 },
  );
}

export async function deleteJournalEntry(journalEntryId: number): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  await httpService.delete(
    `/networks/dashboards/${selectedDashboard}/journal-entries/${journalEntryId}`,
    { validateStatus: (status) => status >= 200 && status < 300 },
  );
}

export async function createContributionPlan(
  plan: CreateContributionPlanDto,
): Promise<ContributionPlanModel> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post<ContributionPlanModel>(`/networks/dashboards/${selectedDashboard}/contribution-plans`, plan)
    .then((res) => res.data);
}

export async function getAllContributionPlans(): Promise<ContributionPlanWithMembersModel[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error("NO_DASHBOARD_SELECTED");
  }
  return httpService
    .get<ContributionPlanWithMembersModel[]>(`/networks/dashboards/${selectedDashboard}/contribution-plans`)
    .then((res) => res.data);
}

export async function createContributionPayment(payment: CreateContributionPaymentDto) {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post(`/networks/dashboards/${selectedDashboard}/contribution-plans/${payment.contributionPlanId}/payments`, payment)
    .then((res) => res.data);
}

export async function getContributionPlanWithPayments(
  contributionPlanId: number,
): Promise<ContributionPlanWithPayments> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .get<ContributionPlanWithPayments>(
      `/networks/dashboards/${selectedDashboard}/contribution-plans/${contributionPlanId}`,
    )
    .then((res) => res.data);
}

export async function getContributionBalancesForConnections(): Promise<
  Record<number, BalanceStats>
> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .get<Record<number, BalanceStats>>(`/networks/dashboards/${selectedDashboard}/contribution-plans/balances`)
    .then((res) => res.data);
}

export async function deleteContributionPlan(contributionPlanId: number): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService.delete(`/networks/dashboards/${selectedDashboard}/contribution-plans/${contributionPlanId}`);
}

export async function getBalanceStatement(): Promise<BalanceSheet> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService.get(`/networks/dashboards/${selectedDashboard}/balance-sheet`).then((res) => res.data);
}

export async function getProfitAndLoss(): Promise<ProfitAndLoss> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService.get(`/networks/dashboards/${selectedDashboard}/profit-and-loss`).then((res) => res.data);
}

// Internal bank transfers
export async function listInternalTransfers(): Promise<InternalBankTransfer[]> {
  return httpService
    .get<InternalBankTransfer[]>(`/business/financial/internal-transfers`)
    .then((res) => res.data);
}

export async function createInternalTransfer(body: Record<string, never> = {}): Promise<InternalBankTransfer> {
  // Note: Internal transfers now use INTERNAL_TRANSFER source type in journal entries
  // Transaction IDs are no longer used
  return httpService
    .post<InternalBankTransfer>(`/business/financial/internal-transfers`, body)
    .then((res) => res.data);
}

export async function updateInternalTransfer(
  id: number,
  body: Record<string, never> = {},
): Promise<InternalBankTransfer> {
  // Note: Internal transfers now use INTERNAL_TRANSFER source type in journal entries
  // Transaction IDs are no longer used
  return httpService
    .patch<InternalBankTransfer>(`/business/financial/internal-transfers/${id}`, body)
    .then((res) => res.data);
}

// Internal transfer counterpart candidates for a mutation
export async function getInternalTransferCandidates(
  mutationId: number,
  params?: { withinDays?: number; strictAmount?: boolean; limit?: number },
): Promise<BankMutation[]> {
  const search = new URLSearchParams();
  const dashboardId = store.getState().application.selectedDashboard;
  if (params?.withinDays !== undefined) search.set('withinDays', String(params.withinDays));
  if (params?.strictAmount !== undefined) search.set('strictAmount', String(params.strictAmount));
  if (params?.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  return httpService
    .get<
      BankMutation[]
    >(`/networks/dashboards/${dashboardId}/mutations/${mutationId}/internal-transfer-candidates${qs ? `?${qs}` : ''}`)
    .then((res) => res.data);
}

// Atomically create both journal entries from two mutations and link them as internal transfer
export async function createInternalTransferFromMutations(
  fromMutationId: number,
  toMutationId: number,
  description?: string,
): Promise<{
  internalTransfer: InternalBankTransfer;
}> {
  // Note: Returns internal transfer only. Journal entries are created with INTERNAL_TRANSFER source type
  return httpService
    .post<{
      internalTransfer: InternalBankTransfer;
    }>(`/business/financial/internal-transfers/from-mutations`, {
      fromMutationId,
      toMutationId,
      description,
    })
    .then((res) => res.data);
}

// Transaction processing
export type ProcessingRequest = {
  dashboardId: number;
};

export type ProcessingStatus = {
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | string;
  jobId?: string;
  message?: string;
  details?: any;
};

export async function processMutations(
  body: ProcessingRequest,
): Promise<{ jobId?: string } & ProcessingStatus> {
  const { data } = await httpService.post<{ jobId?: string } & ProcessingStatus>(
    `/dashboard/${body.dashboardId}/transaction-processing/process-mutations`,
    body,
  );
  return data;
}

// ============================================================================
// Wallet Endpoints (Ledger-based system)
// ============================================================================

export async function getMemberWalletBalance(
  memberId: number,
  currency: Currency,
  asOfDate?: string,
): Promise<WalletBalanceResponse> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  const searchParams = new URLSearchParams();
  searchParams.set('currency', currency);
  if (asOfDate) {
    searchParams.set('asOfDate', asOfDate);
  }
  return httpService
    .get<WalletBalanceResponse>(
      `/networks/dashboards/${selectedDashboard}/wallets/${memberId}/balance?${searchParams.toString()}`,
    )
    .then((res) => res.data);
}

export async function getTotalWalletBalance(
  currency: Currency,
  asOfDate?: string,
): Promise<WalletBalanceResponse> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  const searchParams = new URLSearchParams();
  searchParams.set('currency', currency);
  if (asOfDate) {
    searchParams.set('asOfDate', asOfDate);
  }
  return httpService
    .get<WalletBalanceResponse>(
      `/networks/dashboards/${selectedDashboard}/wallets/total-balance?${searchParams.toString()}`,
    )
    .then((res) => res.data);
}

export async function getWalletAccounts(): Promise<LedgerAccount[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return httpService
    .get<LedgerAccount[]>(`/networks/dashboards/${selectedDashboard}/wallets/accounts`)
    .then((res) => res.data);
}

// ============================================================================
// Saving Pot Endpoints
// ============================================================================

export async function createSavingPot(data: CreateSavingPotDto): Promise<SavingPot> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return httpService
    .post<SavingPot>(`/networks/dashboards/${selectedDashboard}/saving-pots`, data)
    .then((res) => res.data);
}

export async function getAllSavingPots(
  activeOnly: boolean = true,
): Promise<SavingPotWithParticipants[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  const searchParams = new URLSearchParams();
  searchParams.set('activeOnly', String(activeOnly));
  return httpService
    .get<SavingPotWithParticipants[]>(
      `/networks/dashboards/${selectedDashboard}/saving-pots?${searchParams.toString()}`,
    )
    .then((res) => res.data);
}

export async function getSavingPotById(savingPotId: number): Promise<SavingPot | null> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return httpService
    .get<SavingPot | null>(`/networks/dashboards/${selectedDashboard}/saving-pots/${savingPotId}`)
    .then((res) => res.data);
}

export async function updateSavingPot(
  savingPotId: number,
  data: UpdateSavingPotDto,
): Promise<SavingPot> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return httpService
    .patch<SavingPot>(
      `/networks/dashboards/${selectedDashboard}/saving-pots/${savingPotId}`,
      data,
    )
    .then((res) => res.data);
}

export async function deleteSavingPot(savingPotId: number): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return httpService.delete(
    `/networks/dashboards/${selectedDashboard}/saving-pots/${savingPotId}`,
  );
}

export async function fundSavingPot(
  savingPotId: number,
  data: FundSavingPotDto,
): Promise<{ success: boolean }> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return httpService
    .post<{ success: boolean }>(
      `/networks/dashboards/${selectedDashboard}/saving-pots/${savingPotId}/fund`,
      data,
    )
    .then((res) => res.data);
}

export async function releaseFromSavingPot(
  savingPotId: number,
  data: ReleaseSavingPotDto,
): Promise<{ success: boolean }> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return httpService
    .post<{ success: boolean }>(
      `/networks/dashboards/${selectedDashboard}/saving-pots/${savingPotId}/release`,
      data,
    )
    .then((res) => res.data);
}

export async function getMemberSavingPotBalance(
  savingPotId: number,
  memberId: number,
  asOfDate?: string | null,
): Promise<SavingPotBalanceResponse> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  const searchParams = new URLSearchParams();
  if (asOfDate) {
    searchParams.set('asOfDate', asOfDate);
  }
  const queryString = searchParams.toString();
  return httpService
    .get<SavingPotBalanceResponse>(
      `/networks/dashboards/${selectedDashboard}/saving-pots/${savingPotId}/balance/${memberId}${queryString ? `?${queryString}` : ''}`,
    )
    .then((res) => res.data);
}

export async function getTotalSavingPotBalance(
  savingPotId: number,
  asOfDate?: string | null,
): Promise<SavingPotBalanceResponse> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  const searchParams = new URLSearchParams();
  if (asOfDate) {
    searchParams.set('asOfDate', asOfDate);
  }
  const queryString = searchParams.toString();
  return httpService
    .get<SavingPotBalanceResponse>(
      `/networks/dashboards/${selectedDashboard}/saving-pots/${savingPotId}/total-balance${queryString ? `?${queryString}` : ''}`,
    )
    .then((res) => res.data);
}

export async function runSavingPotAutoDeposits(): Promise<RunContributionPlansResponse> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }

  return httpService
    .post<RunContributionPlansResponse>(
      `/networks/dashboards/${selectedDashboard}/saving-pots/auto-deposits/run`,
    )
    .then((res) => res.data);
}

// ============================================================================
// Contribution Plan Endpoints (Ledger-based system)
// ============================================================================

export async function runContributionPlans(
  currency: Currency = Currency.EUR,
): Promise<RunContributionPlansResponse> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  const searchParams = new URLSearchParams();
  searchParams.set('currency', currency);
  return httpService
    .post<RunContributionPlansResponse>(
      `/networks/dashboards/${selectedDashboard}/contribution-plans/run?${searchParams.toString()}`,
    )
    .then((res) => res.data);
}

// ============================================================================
// Financial Categories
// ============================================================================

function requireDashboard(): number {
  const { selectedDashboard } = (store.getState() as RootState).application;
  if (!selectedDashboard) {
    throw new Error('NO_DASHBOARD_SELECTED');
  }
  return selectedDashboard;
}

export async function getCategories(): Promise<FinancialCategory[]> {
  const dashboardId = requireDashboard();
  return httpService
    .get<FinancialCategory[]>(`/networks/dashboards/${dashboardId}/categories`)
    .then((res) => res.data);
}

export async function createCategory(dto: CreateFinancialCategoryDto): Promise<FinancialCategory> {
  const dashboardId = requireDashboard();
  return httpService
    .post<FinancialCategory>(`/networks/dashboards/${dashboardId}/categories`, dto)
    .then((res) => res.data);
}

export async function updateCategory(
  categoryId: number,
  dto: UpdateFinancialCategoryDto,
): Promise<FinancialCategory> {
  const dashboardId = requireDashboard();
  return httpService
    .patch<FinancialCategory>(`/networks/dashboards/${dashboardId}/categories/${categoryId}`, dto)
    .then((res) => res.data);
}

export async function deleteCategory(categoryId: number): Promise<void> {
  const dashboardId = requireDashboard();
  await httpService.delete(`/networks/dashboards/${dashboardId}/categories/${categoryId}`);
}

export async function assignCategoryToLines(
  journalEntryLineIds: number[],
  financialCategoryId: number | null,
): Promise<{ affected: number }> {
  const dashboardId = requireDashboard();
  return httpService
    .post<{ affected: number }>(
      `/networks/dashboards/${dashboardId}/journal-entry-lines/categories`,
      { journalEntryLineIds, financialCategoryId },
    )
    .then((res) => res.data);
}

// ============================================================================
// Book Years
// ============================================================================

export async function getBookYears(): Promise<BookYear[]> {
  const dashboardId = requireDashboard();
  return httpService
    .get<BookYear[]>(`/networks/dashboards/${dashboardId}/book-years`)
    .then((res) => res.data);
}

export async function getCurrentBookYear(): Promise<BookYear | null> {
  const dashboardId = requireDashboard();
  return httpService
    .get<BookYear | null>(`/networks/dashboards/${dashboardId}/book-years/current`)
    .then((res) => res.data);
}

export async function createBookYear(dto: CreateBookYearDto): Promise<BookYear> {
  const dashboardId = requireDashboard();
  return httpService
    .post<BookYear>(`/networks/dashboards/${dashboardId}/book-years`, {
      ...dto,
      startDate: dto.startDate instanceof Date ? dto.startDate.toISOString() : dto.startDate,
      endDate: dto.endDate instanceof Date ? dto.endDate.toISOString() : dto.endDate,
    })
    .then((res) => res.data);
}

export async function updateBookYear(
  bookYearId: number,
  dto: UpdateBookYearDto,
): Promise<BookYear> {
  const dashboardId = requireDashboard();
  const body: Record<string, unknown> = { ...dto };
  if (body.startDate instanceof Date) body.startDate = body.startDate.toISOString();
  if (body.endDate instanceof Date) body.endDate = body.endDate.toISOString();
  return httpService
    .patch<BookYear>(`/networks/dashboards/${dashboardId}/book-years/${bookYearId}`, body)
    .then((res) => res.data);
}

export async function deleteBookYear(bookYearId: number): Promise<void> {
  const dashboardId = requireDashboard();
  await httpService.delete(`/networks/dashboards/${dashboardId}/book-years/${bookYearId}`);
}

// ============================================================================
// Budget
// ============================================================================

export async function getBudget(bookYearId: number): Promise<Budget | null> {
  const dashboardId = requireDashboard();
  return httpService
    .get<Budget | null>(
      `/networks/dashboards/${dashboardId}/book-years/${bookYearId}/budget`,
    )
    .then((res) => res.data);
}

export async function upsertBudget(
  bookYearId: number,
  dto: UpsertBudgetDto,
): Promise<Budget> {
  const dashboardId = requireDashboard();
  return httpService
    .put<Budget>(
      `/networks/dashboards/${dashboardId}/book-years/${bookYearId}/budget`,
      dto,
    )
    .then((res) => res.data);
}

export async function getBudgetReport(bookYearId: number): Promise<BudgetReport> {
  const dashboardId = requireDashboard();
  return httpService
    .get<BudgetReport>(
      `/networks/dashboards/${dashboardId}/book-years/${bookYearId}/budget/report`,
    )
    .then((res) => res.data);
}

// ============================================================================
// P&L + Balance (with BookYear)
// ============================================================================

export async function getProfitAndLossDetailed(
  bookYearId: number,
): Promise<ProfitAndLossDetailed> {
  const dashboardId = requireDashboard();
  return httpService
    .get<ProfitAndLossDetailed>(
      `/networks/dashboards/${dashboardId}/profit-and-loss?bookYearId=${bookYearId}`,
    )
    .then((res) => res.data);
}

export async function getProfitAndLossForDateRange(
  fromDate: Date,
  toDate: Date,
): Promise<ProfitAndLossDetailed> {
  const dashboardId = requireDashboard();
  const params = new URLSearchParams();
  params.set('fromDate', fromDate.toISOString());
  params.set('toDate', toDate.toISOString());
  return httpService
    .get<ProfitAndLossDetailed>(
      `/networks/dashboards/${dashboardId}/profit-and-loss?${params.toString()}`,
    )
    .then((res) => res.data);
}

export async function getBalanceSheetForBookYear(
  bookYearId: number,
): Promise<BalanceSheetWithBookYear> {
  const dashboardId = requireDashboard();
  return httpService
    .get<BalanceSheetWithBookYear>(
      `/networks/dashboards/${dashboardId}/balance-sheet?bookYearId=${bookYearId}`,
    )
    .then((res) => res.data);
}

// ============================================================================
// Categorization Rules
// ============================================================================

export async function listCategorizationRules(): Promise<CategorizationRule[]> {
  const dashboardId = requireDashboard();
  return httpService
    .get<CategorizationRule[]>(`/networks/dashboards/${dashboardId}/categorization-rules/`)
    .then((res) => res.data);
}

export async function createCategorizationRule(
  dto: CreateCategorizationRuleDto,
): Promise<CategorizationRule> {
  const dashboardId = requireDashboard();
  return httpService
    .post<CategorizationRule>(`/networks/dashboards/${dashboardId}/categorization-rules/`, dto)
    .then((res) => res.data);
}

export async function updateCategorizationRule(
  ruleId: number,
  dto: UpdateCategorizationRuleDto,
): Promise<CategorizationRule> {
  const dashboardId = requireDashboard();
  return httpService
    .patch<CategorizationRule>(
      `/networks/dashboards/${dashboardId}/categorization-rules/${ruleId}`,
      dto,
    )
    .then((res) => res.data);
}

export async function deleteCategorizationRule(ruleId: number): Promise<void> {
  const dashboardId = requireDashboard();
  await httpService.delete(
    `/networks/dashboards/${dashboardId}/categorization-rules/${ruleId}`,
    { validateStatus: (s) => s >= 200 && s < 300 },
  );
}

export async function applyCategorizationRulesToAll(): Promise<CategorizationRuleApplyResult> {
  const dashboardId = requireDashboard();
  return httpService
    .put<CategorizationRuleApplyResult>(
      `/networks/dashboards/${dashboardId}/categorization-rules/apply-all`,
      {},
    )
    .then((res) => res.data);
}
